/**
 * Sorting House item vocabulary. Each visual kind maps to one art asset and
 * a fixed set of base tags. Levels may add `color:*` (tintable kinds only)
 * and `size:*` tags on top. Changing base tags changes puzzle meaning, so
 * it requires a new content version.
 */

export const SORT_TYPES = [
  "toy",
  "plush",
  "candy",
  "cake",
  "cookie",
  "fruit",
  "garden",
  "night",
  "magic",
  "gem",
  "balloon",
  "craft",
  "gift",
  "doll",
] as const;
export type SortType = (typeof SORT_TYPES)[number];

export const SORT_SHAPES = ["star", "heart", "round", "drop"] as const;
export type SortShape = (typeof SORT_SHAPES)[number];

export const SORT_COLORS = ["pink", "blue", "yellow", "green", "purple", "orange"] as const;
export type SortColor = (typeof SORT_COLORS)[number];

export const SORT_SIZES = ["s", "m", "l", "xl"] as const;
export type SortSize = (typeof SORT_SIZES)[number];

/** Multiply tints tuned for the pearl-white tintable art. */
export const COLOR_TINTS: Record<SortColor, string> = {
  pink: "#ff8cc6",
  blue: "#68c3ff",
  yellow: "#ffd84a",
  green: "#6ddc8c",
  purple: "#b98cff",
  orange: "#ffa75a",
};

export const SIZE_RANK: Record<SortSize, number> = { s: 1, m: 2, l: 3, xl: 4 };

export interface ItemKind {
  readonly id: string;
  readonly assetId: string;
  readonly type: SortType;
  readonly shape?: SortShape;
  readonly tintable: boolean;
  readonly special?: "switch";
}

const kind = (
  id: string,
  type: SortType,
  extra: { shape?: SortShape; tintable?: boolean; special?: "switch" } = {},
): ItemKind => ({
  id,
  assetId: `item.${id}`,
  type,
  tintable: extra.tintable ?? false,
  ...(extra.shape ? { shape: extra.shape } : {}),
  ...(extra.special ? { special: extra.special } : {}),
});

export const ITEM_KINDS: readonly ItemKind[] = [
  kind("ball", "toy"),
  kind("toy-car", "toy"),
  kind("duck", "toy"),
  kind("robot", "toy"),
  kind("blocks", "toy"),
  kind("top", "toy"),
  kind("teddy", "plush"),
  kind("bunny", "plush"),
  kind("kitty", "plush"),
  kind("candy", "candy"),
  kind("lollipop", "candy"),
  kind("candy-cane", "candy"),
  kind("cupcake", "cake"),
  kind("donut", "cake"),
  kind("cake-slice", "cake"),
  kind("croissant", "cake"),
  kind("macaron", "cake"),
  kind("strawberry", "fruit"),
  kind("cherry", "fruit"),
  kind("apple", "fruit"),
  kind("flower", "garden"),
  kind("mushroom", "garden"),
  kind("leaf", "garden"),
  kind("acorn", "garden"),
  kind("butterfly", "garden"),
  kind("lantern", "night"),
  kind("moon", "night"),
  kind("star-charm", "night", { shape: "star" }),
  kind("candle", "night"),
  kind("potion", "magic"),
  kind("wand", "magic"),
  kind("crystal-ball", "magic"),
  kind("gem-star", "gem", { shape: "star", tintable: true }),
  kind("gem-heart", "gem", { shape: "heart", tintable: true }),
  kind("gem-round", "gem", { shape: "round", tintable: true }),
  kind("gem-drop", "gem", { shape: "drop", tintable: true }),
  kind("balloon-star", "balloon", { shape: "star", tintable: true }),
  kind("balloon-heart", "balloon", { shape: "heart", tintable: true }),
  kind("balloon-round", "balloon", { shape: "round", tintable: true }),
  kind("bow", "craft", { tintable: true }),
  kind("button", "craft", { shape: "round", tintable: true }),
  kind("pompom", "craft", { shape: "round", tintable: true }),
  kind("gift", "gift", { tintable: true }),
  kind("doll", "doll", { tintable: true }),
  kind("cookie-star", "cookie", { shape: "star" }),
  kind("cookie-heart", "cookie", { shape: "heart" }),
  kind("cookie-round", "cookie", { shape: "round" }),
  kind("bell", "magic", { special: "switch" }),
];

export const ITEM_KIND_BY_ASSET: ReadonlyMap<string, ItemKind> = new Map(
  ITEM_KINDS.map((k) => [k.assetId, k]),
);
export const ITEM_KIND_BY_ID: ReadonlyMap<string, ItemKind> = new Map(ITEM_KINDS.map((k) => [k.id, k]));

export function baseTagsOf(kindDef: ItemKind): string[] {
  if (kindDef.special === "switch") return ["special:switch"];
  const tags = [`type:${kindDef.type}`];
  if (kindDef.shape) tags.push(`shape:${kindDef.shape}`);
  return tags;
}

/** Kinds shown on a bin label for a `type:*` rule. */
export const TYPE_ICON_KINDS: Record<SortType, readonly string[]> = {
  toy: ["duck", "ball"],
  plush: ["teddy", "bunny"],
  candy: ["candy", "lollipop"],
  cake: ["cupcake", "donut"],
  cookie: ["cookie-round", "cookie-star"],
  fruit: ["strawberry", "apple"],
  garden: ["flower", "leaf"],
  night: ["moon", "lantern"],
  magic: ["potion", "wand"],
  gem: ["gem-round", "gem-star"],
  balloon: ["balloon-round", "balloon-heart"],
  craft: ["bow", "button"],
  gift: ["gift"],
  doll: ["doll"],
};

export const TAG_LABELS_VI: Record<string, string> = {
  "type:toy": "Đồ chơi",
  "type:plush": "Thú bông",
  "type:candy": "Kẹo",
  "type:cake": "Bánh ngọt",
  "type:cookie": "Bánh quy",
  "type:fruit": "Trái cây",
  "type:garden": "Khu vườn",
  "type:night": "Đồ đêm",
  "type:magic": "Phép thuật",
  "type:gem": "Đá quý",
  "type:balloon": "Bóng bay",
  "type:craft": "Đồ may",
  "type:gift": "Hộp quà",
  "type:doll": "Búp bê",
  "shape:star": "Sao",
  "shape:heart": "Tim",
  "shape:round": "Tròn",
  "shape:drop": "Giọt",
  "color:pink": "Hồng",
  "color:blue": "Xanh dương",
  "color:yellow": "Vàng",
  "color:green": "Xanh lá",
  "color:purple": "Tím",
  "color:orange": "Cam",
  "size:s": "Nhỏ",
  "size:m": "Vừa",
  "size:l": "Lớn",
  "size:xl": "Rất lớn",
  "special:switch": "Chuông phép",
};

export function isKnownTag(tag: string): boolean {
  return tag in TAG_LABELS_VI;
}
